import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputAdornment,
  OutlinedInput,
  Radio,
  useMediaQuery
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import InfoTooltip from '@components/info-tooltip/info-tooltip.tsx';
import AppAutocompleteV2 from '@components/app-autocomplete/app-autocomplete-v2.tsx';
import { ScreenBadge } from '@shared/utils/screen-badge.utils.tsx';
import { CrudActionEnum } from '@models/global.model.ts';
import TabsComponent from '@components/tabs/tabs.tsx';
import { validateNotOnlySpaces } from '@utils/trim-form-values-utils.ts';
import { useErrorMessage } from '@hooks/use-error-message.ts';
import useProfileStore from '@store/profile-store/profile.store.ts';
import useLanguageStore from '@store/language-store/language.store.tsx';
import { LanguageEnum } from '@models/language.model.ts';
import { ServicesModel, ServiceTypeEnum } from '@models/services.model.ts';
import { servicesErrorMessages } from '@modules/services/utils/services.utils.ts';
import TimeRangePickerList from '@components/time-range-picker-list/time-range-picker-list.tsx';
import { TimeRange } from '@models/time-range-picker.model.ts';
import { convertToSelectedDepartmentUnit } from '@modules/services/utils/services.utils.ts';
import useServicesStore from '@modules/services/store/services.store.ts';
import TimeEstimateInput from '@components/time-estimate-input/time-estimate-input.tsx';
import dayjs from 'dayjs';
import useUnitsStore from '@modules/departments-and-units/pages/units/store/units.store';
import useDepartmentStore from '@modules/departments-and-units/pages/departments/store/departments.store';
import { UnitsModel } from '@shared/models/departments-and-units/units.model';
import UnitsDialogContent from '@modules/departments-and-units/pages/units/components/units-dialog-content/units-dialog-content';
import AppDialog from '@shared/components/app-dialog/app-dialog';
import { FileTypeEnum } from '@models/media-library.model.ts';
import MediaLibraryComponent from '@components/media-library-component/media-library-component.tsx';
import useMediaLibraryStore from '@store/media-library-store/media-library.store.ts';

const ServicesDialogContent: React.FC<{
  onSave: (data: ServicesModel) => void;
  updateServiceData: any | null;
  departmentUnit: any;
}> = ({ onSave, updateServiceData, departmentUnit }) => {
  const { t } = useTranslation();
  const getErrorMessage = useErrorMessage();
  const { isLoading, getDepartmentUnit } = useServicesStore();
  const {
    register,
    formState: { errors, isValid },
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    clearErrors
  } = useForm<any>({ mode: 'onTouched' });
  const { createUnit } = useUnitsStore();
  const { departmentsList, getDepartmentsList } = useDepartmentStore();
  const { selectedCurrency } = useProfileStore();
  const { languageList } = useLanguageStore();
  const { resetSelectedMedia } = useMediaLibraryStore();
  const defaultLang = languageList[0]?.value || LanguageEnum.English;
  const [currentTab, setCurrentTab] = useState<string>(defaultLang);
  // const [timeRanges, setTimeRanges] = useState<TimeRange[]>([]);
  const [updateUnit, setUpdateUnit] = useState<UnitsModel | null>(null);
  const [openDialog, setOpenDialog] = useState<boolean>(false);

  const [multiLangFields, setMultiLangFields] = useState<{
    name: Record<string, string | undefined>;
    description: Record<string, string | undefined>;
  }>({
    name: {},
    description: {}
  });
  const [selectedMedia, setSelectedMedia] = React.useState<Record<string, any>>(
    {}
  );
  const isTabletScreen = useMediaQuery('(max-width: 1715px)');
  const [serviceType, setServiceType] = useState<ServiceTypeEnum>(
    ServiceTypeEnum.Reservable
  );

  const selectedDepartmentUnit = useMemo(() => {
    return convertToSelectedDepartmentUnit(updateServiceData);
  }, [updateServiceData]);

  const watchedDepartmentUnit = watch('departmentUnit');

  const computedSelectedList = useMemo(() => {
    if (watchedDepartmentUnit) {
      return Array.isArray(watchedDepartmentUnit)
        ? watchedDepartmentUnit
        : [watchedDepartmentUnit];
    }
    return [];
  }, [watchedDepartmentUnit]);

  useEffect(() => {
    if (updateServiceData) {
      const {
        name = {},
        description = {},
        price,
        duration,
        type = ServiceTypeEnum.Reservable
      } = updateServiceData;

      setServiceType(type);
      setMultiLangFields({ name, description });

      reset({
        name: name[defaultLang] || '',
        description: description[defaultLang] || '',
        price,
        departmentUnit: selectedDepartmentUnit[0] || null,
        duration
      });

      // if (departmentUnit?.availableHours?.length) {
      //   const converted: TimeRange[] = departmentUnit.availableHours.map(
      //     (item: any) => ({
      //       start: dayjs(item.open, 'HH:mm'),
      //       end: dayjs(item.close, 'HH:mm')
      //     })
      //   );
      //   setTimeRanges(converted);
      // }
    }
  }, [updateServiceData, reset, defaultLang]);

  // const handleTimeChange = (ranges: TimeRange[]) => {
  //   setTimeRanges(ranges);
  // };

  const handleChangeLanguage = (_e: React.SyntheticEvent, newLang: string) => {
    const currentName = watch('name');
    const currentDescription = watch('description');

    setMultiLangFields((prev) => ({
      name: { ...prev.name, [currentTab]: currentName },
      description: { ...prev.description, [currentTab]: currentDescription }
    }));

    setCurrentTab(newLang);
    setValue('name', multiLangFields.name?.[newLang] || '');
    setValue('description', multiLangFields.description?.[newLang] || '');
  };

  const handleFormSave = async (row: any) => {
    try {
      const res: any = await createUnit({
        data: row,
        handleClose: () => {
          setOpenDialog(false);
          setUpdateUnit(null);
        }
      });

      const unitData = res?.data || res;

      const newOption = {
        id: unitData?.id || unitData?._id,
        title: unitData?.name || unitData?.title,
        type: unitData?.type
      };

      setValue('departmentUnit', newOption, {
        shouldValidate: true,
        shouldDirty: true
      });

      await getDepartmentUnit();

      return newOption;
    } catch (error) {
      return null;
    }
  };

  const onSubmit = (data: any) => {
    const currentName = watch('name');
    const currentDescription = watch('description');

    const departmentUnitId = Array.isArray(data.departmentUnit)
      ? data.departmentUnit[0]?.id
      : data.departmentUnit?.id;

    // const availableHours = timeRanges
    //   .filter(
    //     (range): range is { start: dayjs.Dayjs; end: dayjs.Dayjs } =>
    //       range.start !== null && range.end !== null
    //   )
    //   .map((range) => ({
    //     open: range.start.format('HH:mm'),
    //     close: range.end.format('HH:mm')
    //   }));

    const fullData: any = {
      departmentUnitId,
      price: data.price,
      name: { ...multiLangFields.name, [currentTab]: currentName },
      description: {
        ...multiLangFields.description,
        [currentTab]: currentDescription
      },
      // availableHours,
      image: selectedMedia.serviceImage?.path || null,
      type: serviceType
    };

    if (data.duration && data.duration.trim() !== '') {
      fullData.duration = data.duration;
    }

    onSave(fullData);
  };

  useEffect(() => {
    getDepartmentsList();
  }, []);

  useEffect(() => {
    if (serviceType === ServiceTypeEnum.Requestable) {
      clearErrors('duration');
    }
  }, [serviceType]);

  useEffect(() => {
    return () => {
      resetSelectedMedia('serviceImage');
    };
  }, []);

  return (
    <div>
      <div className="w-full">
        <TabsComponent
          tabs={languageList}
          value={currentTab}
          onChange={handleChangeLanguage}
        />
      </div>

      <div className="mt-16 flex gap-8 max-lg-xl:mt-10">
        <div className="media-wrapper">
          <div className="flex mb-1.5">
            <span className="reuqired-symbole">*</span>
            {t('label_image')}
          </div>

          <MediaLibraryComponent
            mediaKey={'serviceImage'}
            allowedTypes={[FileTypeEnum.Image]}
            path={updateServiceData?.image}
            onSelect={(media) =>
              setSelectedMedia((prev) => ({ ...prev, serviceImage: media }))
            }
          />
        </div>

        <form className="w-full flex flex-col gap-1">
          <FormControl error={!!errors.name}>
            <FormLabel className="flex">
              <span className="reuqired-symbole">*</span>
              {t('label_service_name')} <InfoTooltip text="tooltip_info_name" />
            </FormLabel>
            <OutlinedInput
              {...register('name', {
                required: true,
                validate: { notOnlySpaces: validateNotOnlySpaces }
              })}
              error={!!errors.name}
              placeholder={t('placeholder_name')}
            />
            <FormHelperText>
              {errors?.name &&
                getErrorMessage({
                  errors,
                  fieldName: 'name',
                  errorMessages: servicesErrorMessages
                })}
            </FormHelperText>
          </FormControl>

          <FormControl error={!!errors.price}>
            <FormLabel className="flex">
              <span className="reuqired-symbole">*</span>
              {t('label_package_price')}
              <InfoTooltip text="rooms_packages_management_package_price_tooltip" />
            </FormLabel>
            <OutlinedInput
              className="no-spinner"
              type="number"
              {...register('price', {
                required: true,
                valueAsNumber: true
              })}
              startAdornment={
                <InputAdornment position="start">
                  {selectedCurrency?.symbol}
                </InputAdornment>
              }
              placeholder={t('placeholder_price')}
            />
            <FormHelperText>
              {errors?.price &&
                getErrorMessage({
                  errors,
                  fieldName: 'price',
                  errorMessages: servicesErrorMessages
                })}
            </FormHelperText>
          </FormControl>

          <AppAutocompleteV2
            key={departmentUnit}
            name="departmentUnit"
            control={control}
            selectedList={computedSelectedList}
            label={
              <span className="flex items-center gap-2">
                <span className="flex items-center">
                  <span className="reuqired-symbole">*</span>
                  {t('label_department_unit')}
                  <InfoTooltip text="tooltip_info_department_unit" />
                </span>
                {watchedDepartmentUnit && (
                  <ScreenBadge type={watchedDepartmentUnit?.type} />
                )}
              </span>
            }
            placeholder={t('placeholder_department_unit')}
            options={departmentUnit}
            withChip={false}
            addItemConfig={{
              action: CrudActionEnum.AddWithDialog,
              callback: (data: any) => {
                setOpenDialog(true);
                setUpdateUnit({ name: data.title });
              }
            }}
            validationProps={{
              errors,
              errorMessages: servicesErrorMessages,
              getErrorMessage,
              validationRules: { required: true }
            }}
            renderOptionBadge={(option) => <ScreenBadge type={option?.type} />}
          />
          {/*
          <div className="w-full max-w-[600px] max-lg-xl:max-w-[380px]">
            <TimeRangePickerList
              updateData={timeRanges}
              onChange={handleTimeChange}
              mode="multiple"
            />
          </div> */}

          <TimeEstimateInput
            key={serviceType}
            name="duration"
            label={
              <span className="flex items-center gap-1">
                {serviceType === ServiceTypeEnum.Reservable && (
                  <span className="reuqired-symbole">*</span>
                )}
                {t('label_duration')}
                <InfoTooltip text={t('tooltip_info_duration')} />
              </span>
            }
            placeholder={t('placeholder_duration')}
            control={control}
            validationProps={{
              errors,
              errorMessages: servicesErrorMessages,
              getErrorMessage,
              validationRules: {
                required: serviceType === ServiceTypeEnum.Reservable
              }
            }}
          />

          <Box className="mt-2.5 mb-3 flex gap-6">
            <Box
              onClick={() => setServiceType(ServiceTypeEnum.Reservable)}
              className={`cursor-pointer w-full border rounded-lg py-3 px-2.5 transition-all ${
                serviceType === ServiceTypeEnum.Reservable
                  ? 'border-(--primary) bg-(--primary-50)'
                  : 'border-(--blue-12) bg-(--gray-11)'
              }`}
            >
              <FormControlLabel
                className="items-start!"
                control={
                  <Radio
                    className="py-0!"
                    checked={serviceType === ServiceTypeEnum.Reservable}
                    onChange={() => setServiceType(ServiceTypeEnum.Reservable)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <p className="font-semibold text-sm text-[var(--dark-2)]">
                      {t('services_dialog_content_radio_reservable_title')}
                    </p>
                    <p className="text-(--dark-alpha-600) text-[12px] mt-1">
                      {t('services_dialog_content_radio_reservable_desc')}
                    </p>
                  </Box>
                }
              />
            </Box>

            <Box
              onClick={() => setServiceType(ServiceTypeEnum.Requestable)}
              className={`cursor-pointer w-full border rounded-lg py-3 px-2.5 transition-all ${
                serviceType === ServiceTypeEnum.Requestable
                  ? 'border-(--primary) bg-(--primary-50)'
                  : 'border-(--blue-12) bg-(--gray-11)'
              }`}
            >
              <FormControlLabel
                className="items-start!"
                control={
                  <Radio
                    className="py-0!"
                    checked={serviceType === ServiceTypeEnum.Requestable}
                    onChange={() => setServiceType(ServiceTypeEnum.Requestable)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <p className="font-semibold text-sm text-[var(--dark-2)]">
                      {t('services_dialog_content_radio_requestable_title')}
                    </p>
                    <p className="text-(--dark-alpha-600) text-[12px] mt-1">
                      {t('services_dialog_content_radio_requestable_desc')}
                    </p>
                  </Box>
                }
              />
            </Box>
          </Box>

          <FormControl error={!!errors.description}>
            <FormLabel className="flex">
              <span className="reuqired-symbole">*</span>
              {t('label_description')}
              <InfoTooltip text="tooltip_info_notes" />
            </FormLabel>
            <OutlinedInput
              multiline
              rows={5}
              {...register('description', {
                required: true,
                validate: { notOnlySpaces: validateNotOnlySpaces }
              })}
              placeholder={t('placeholder_notes')}
            />
            <FormHelperText>
              {errors?.description &&
                getErrorMessage({
                  errors,
                  fieldName: 'description',
                  errorMessages: servicesErrorMessages
                })}
            </FormHelperText>
          </FormControl>

          <div className="flex justify-end pt-4 sticky pb-1 bottom-0 bg-(--white)">
            <Button
              variant="contained"
              loading={isLoading()}
              disabled={!isValid || !selectedMedia?.serviceImage}
              onClick={handleSubmit(onSubmit)}
            >
              {t('save')}
            </Button>
          </div>
        </form>
      </div>
      <AppDialog
        open={openDialog}
        size={isTabletScreen ? 'sm' : 'md'}
        title={t('unit_dialog_title')}
        content={
          <UnitsDialogContent
            departments={departmentsList}
            updateUnitData={updateUnit}
            onSave={handleFormSave}
          />
        }
        onClose={() => setOpenDialog(false)}
      />
    </div>
  );
};

export default ServicesDialogContent;
